import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import './Header.scss';
import get from 'lodash.get';
import { getNavigationUrl, getUrlParams } from '../../utils/shared/urlUtils';
import { SECTION_IDS } from '../../constants/shared/CommonConstants';
import LinkWrapper from '../navigation/LinkWrapper';
import DropDown from '../../components/dropdown/DropDown';
import TopMenuItem from './TopMenuItem';
import DisableComponentWithPopover from '../../components/disableComponentWithPopover/DisableComponentWithPopover';
import AdditionalLinkPopoverMessage from './AdditionalLinkPopoverMessage';
import { generateReactKey } from '../../utils/shared/commonUtils';
import { lrsSelectedHeaderDropdownProps } from '../lrs/shared/lrsHelpers';
import { CLASS_DROPDOWN } from '../../constants/reading/DemoModeConstants';

const { MCLASS_HEADER } = SECTION_IDS;

const Header = ({
    licensingInfo,
    location,
    districtDropDownProps,
    schoolDropDownProps,
    classDropDownProps,
    additionalURLParams,
    topMenuItems,
    views,
    appUrl,
    getPageActiveClass,
    isStaffConfigFetched,
    lrsSelectedHeaderDropdownEvent,
    headerLogoClassNames,
    isDemoModeOn,
    headerHasErrorClassName,
    headerInDemoClassName,
    officialTeacher,
    downloadDemoPdf,
    selectedPeriod,
    demoClass,
    isGuidedTourOn,
}) => {
    const mainMenuItems = [];
    Object.entries(views).forEach(view => {
        const [viewKey, viewConfig] = view;
        const additionalURLParamObj = viewConfig.additionalURLParams
            ? viewConfig.additionalURLParams(licensingInfo)
            : {};
        if (viewConfig.enabledWhen(licensingInfo)) {
            const pageActiveClass = getPageActiveClass(viewKey);

            const disabledItem = (
                <DisableComponentWithPopover
                    key={generateReactKey(viewKey)}
                    popoverMessage={AdditionalLinkPopoverMessage(
                        get(viewConfig, 'withPopover.url'),
                        get(viewConfig, 'withPopover.message'),
                    )}
                    disabled
                    className={`nav-item nav-link disabled ${viewKey}`}
                    popperConfig={{
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, -24],
                                },
                            },
                        ],
                    }}
                >
                    <span className="disabled">{viewConfig.menuItemName}</span>
                </DisableComponentWithPopover>
            );
            const menuItem =
                isEmpty(licensingInfo) || viewConfig.isDisabled(licensingInfo) ? (
                    disabledItem
                ) : (
                    <LinkWrapper
                        className={`nav-item nav-link ${viewKey} ${pageActiveClass}`}
                        key={generateReactKey(viewKey)}
                        configName={viewConfig.menuItemName}
                        path={`${appUrl}/${getNavigationUrl(location, viewKey, {
                            ...additionalURLParams,
                            ...additionalURLParamObj,
                        })}`}
                        content={viewConfig.menuItemName}
                    />
                );
            mainMenuItems.push(menuItem);
        }
    });

    useEffect(() => {
        if (!isEmpty(lrsSelectedHeaderDropdownProps.data)) {
            const { classe, district, school } = getUrlParams(location);
            lrsSelectedHeaderDropdownEvent({
                ...lrsSelectedHeaderDropdownProps.data,
                classe,
                district,
                school,
            });

            lrsSelectedHeaderDropdownProps.setData({});
        }
    }, [location]);

    return isStaffConfigFetched ? (
        <section
            id={MCLASS_HEADER}
            className={`header ${headerHasErrorClassName} ${headerInDemoClassName}`}
        >
            <div className="print-only">
                <div className="teacher-name">
                    <div className="teacher-profile-url">{`${officialTeacher.teacherName} ${officialTeacher.teacherSurname}`}</div>
                </div>
            </div>
            <div className="filter-section-wrapper">
                <div className={`mclass-logo ${headerLogoClassNames} no-print`} />
                <div
                    className={`${location.pathname.includes('duallanguage') ? 'no-print' : ''} 
                filter-section`}
                    data-tour-step={CLASS_DROPDOWN}
                >
                    <DropDown {...districtDropDownProps} isDemoModeOn={isDemoModeOn} />
                    <DropDown {...schoolDropDownProps} isDemoModeOn={isDemoModeOn} />
                    <DropDown {...classDropDownProps} isDemoModeOn={isDemoModeOn} />
                </div>
            </div>
            <nav className="navlinks-wrapper">
                <div className="nav nav-horizontal">{mainMenuItems}</div>
                <nav className="right header-icons">
                    {topMenuItems.map((item, index) => (
                        <TopMenuItem
                            key={generateReactKey(index)}
                            {...item}
                            isDemoModeOn={isDemoModeOn}
                            downloadDemoPdf={downloadDemoPdf}
                            selectedPeriod={selectedPeriod}
                            demoClass={demoClass}
                            isGuidedTourOn={isGuidedTourOn}
                        />
                    ))}
                </nav>
            </nav>
        </section>
    ) : (
        <></>
    );
};

Header.defaultProps = {
    location: {},
    licensingInfo: [],
    headerLogoClassNames: '',
    isStaffConfigFetched: '',
    additionalURLParams: {},
    topMenuItems: [],
    views: {},
    appUrl: '',
    officialTeacher: {},
    getPageActiveClass: () => '',
    lrsSelectedHeaderDropdownEvent: () => {},
    isDemoModeOn: false,
    headerHasErrorClassName: '',
    headerInDemoClassName: '',
    downloadDemoPdf: () => {},
    selectedPeriod: '',
    demoClass: '',
    isGuidedTourOn: false,
};

Header.propTypes = {
    licensingInfo: PropTypes.array,
    headerLogoClassNames: PropTypes.string,
    isStaffConfigFetched: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    location: PropTypes.object,
    districtDropDownProps: PropTypes.object.isRequired,
    schoolDropDownProps: PropTypes.object.isRequired,
    classDropDownProps: PropTypes.object.isRequired,
    additionalURLParams: PropTypes.object,
    topMenuItems: PropTypes.array,
    views: PropTypes.object,
    appUrl: PropTypes.string,
    officialTeacher: PropTypes.object,
    getPageActiveClass: PropTypes.func,
    lrsSelectedHeaderDropdownEvent: PropTypes.func,
    isDemoModeOn: PropTypes.bool,
    headerHasErrorClassName: PropTypes.string,
    headerInDemoClassName: PropTypes.string,
    downloadDemoPdf: PropTypes.func,
    selectedPeriod: PropTypes.string,
    demoClass: PropTypes.string,
    isGuidedTourOn: PropTypes.bool,
};

export default Header;
